import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import { Flex } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import type { TimeGranularitySymbol } from "app/common/utils/timeGranularity";
import DurationService from "app/services/DurationService";
import Chart from "pro-features/components/Chart/src/Chart";
import { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import { AverageChart } from "app/pages/industry-analysis/players/market-shares/AverageChart";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

import { getChartConfig } from "./getChartConfig";
import type { EnrichedData, IChart, IRawData, SwitchGraphDomainData } from "./types";
import { EChartType } from "./types";
import { getChartData } from "./utils/parseRawData";
import { getApiKey } from "./utils/utils";

interface IMarketSharesChartProps {
    chartType: EChartType;
    chartDisplayType: EChartDisplayType;
    parsedData: IRawData;
    compareData?: IRawData;
    enrichedData: EnrichedData;
    rawDomainData: SwitchGraphDomainData[];
    legendItems: ILegendItems;
    timeGranularity: TimeGranularitySymbol;
    isMonthToDate: boolean;
    showOthers: boolean;
}

export const MarketSharesChart = ({
    chartType,
    chartDisplayType,
    parsedData,
    compareData,
    enrichedData,
    rawDomainData,
    legendItems,
    timeGranularity,
    isMonthToDate,
    showOthers,
}: IMarketSharesChartProps) => {
    const { params } = useCurrentStateAndParams();
    const toDate = DurationService.getDurationData(params.duration).raw.to;
    const isPercentage = chartDisplayType === EChartDisplayType.PERCENTAGE;
    const segmentsModule: ISegmentsModule = useSelector((state) => {
        const { segmentsModule } = state;
        return segmentsModule;
    });
    const getUserAndAccountSegmentById = useMemo(
        (): any => SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule),
        [],
    );
    const chartData = useMemo((): IChart[] => {
        const ApiDisplayType = getApiKey(chartDisplayType);
        const lastSupportedDate = isMonthToDate ? swSettings.current.lastSupportedDailyDate : toDate;
        return getChartData(
            legendItems,
            parsedData[ApiDisplayType],
            showOthers,
            timeGranularity,
            isMonthToDate,
            lastSupportedDate,
        );
    }, [chartDisplayType, legendItems, parsedData, showOthers, timeGranularity, isMonthToDate, toDate]);
    const compareChartData = useMemo((): IChart[] => {
        if (!compareData) {
            return [];
        }

        const ApiDisplayType = getApiKey(chartDisplayType);
        const lastSupportedDate = isMonthToDate ? swSettings.current.lastSupportedDailyDate : toDate;
        return getChartData(
            legendItems,
            compareData[ApiDisplayType],
            showOthers,
            timeGranularity,
            isMonthToDate,
            lastSupportedDate,
        );
    }, [compareData, chartDisplayType, isMonthToDate, toDate, legendItems, showOthers, timeGranularity]);
    const chartConfig = useMemo(
        () =>
            getChartConfig(
                chartData,
                isPercentage,
                getUserAndAccountSegmentById,
                enrichedData,
                timeGranularity,
                toDate,
                isMonthToDate,
            ),
        [chartData, isPercentage, getUserAndAccountSegmentById, enrichedData, timeGranularity, toDate, isMonthToDate],
    );

    return (
        <Flex
            data-automation="market-shares-chart"
            flex="1"
            flexDirection="column"
            height="100%"
        >
            {chartType === EChartType.OVERTIME ? (
                <Chart
                    type={chartTypes.AREA}
                    data={structuredClone(chartData).reverse()}
                    config={chartConfig}
                />
            ) : (
                <AverageChart
                    chartData={chartData}
                    compareChartData={compareChartData}
                    enrichedData={enrichedData}
                    rawDomainData={rawDomainData}
                    chartDisplayType={chartDisplayType}
                />
            )}
        </Flex>
    );
};
